/* global IDBDatabase, IDBTransaction, IDBTransactionMode, indexedDB */
import type { LifeOsState } from "../types";
import { migrateState } from "./storage";

const DB_NAME = "personal-life-os";
const DB_VERSION = 1;
const STORE_NAME = "stateSnapshots";

type IndexedStateRecord = {
  storageKey: string;
  state: LifeOsState;
  updatedAt: string;
};

function hasIndexedDb() {
  return typeof indexedDB !== "undefined";
}

// One shared connection per tab: opening/closing on every read/write serializes
// each operation behind connection setup. The cache self-heals — it is dropped
// on open failure, on a forced close, and on a versionchange from another tab.
let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
  if (!hasIndexedDb()) {
    return Promise.resolve(null);
  }

  if (!dbPromise) {
    dbPromise = new Promise((resolve) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: "storageKey" });
        }
      };
      request.onerror = () => {
        dbPromise = null; // do not cache the failure — the next call retries
        resolve(null);
      };
      request.onsuccess = () => {
        const db = request.result;
        // Another tab requested a version upgrade: release this connection so
        // the upgrade can proceed; the next operation reopens at the new version.
        db.onversionchange = () => {
          db.close();
          dbPromise = null;
        };
        // Browser force-closed the connection (storage eviction / recovery).
        db.onclose = () => {
          dbPromise = null;
        };
        resolve(db);
      };
    });
  }

  return dbPromise;
}

/** Start a transaction on the shared connection; null when it was closed under us. */
function startTransaction(db: IDBDatabase, mode: IDBTransactionMode): IDBTransaction | null {
  try {
    return db.transaction(STORE_NAME, mode);
  } catch {
    dbPromise = null; // stale handle — the next call reopens
    return null;
  }
}

export async function saveIndexedState(storageKey: string, state: LifeOsState): Promise<boolean> {
  const db = await openDb();
  if (!db) {
    return false;
  }
  const tx = startTransaction(db, "readwrite");
  if (!tx) {
    return false;
  }

  return new Promise((resolve) => {
    tx.objectStore(STORE_NAME).put({ storageKey, state, updatedAt: state.updatedAt } satisfies IndexedStateRecord);
    tx.oncomplete = () => resolve(true);
    tx.onerror = () => resolve(false);
    tx.onabort = () => resolve(false);
  });
}

export async function loadIndexedState(storageKey: string): Promise<LifeOsState | null> {
  const db = await openDb();
  if (!db) {
    return null;
  }
  const tx = startTransaction(db, "readonly");
  if (!tx) {
    return null;
  }

  return new Promise((resolve) => {
    const request = tx.objectStore(STORE_NAME).get(storageKey);
    request.onsuccess = () => {
      const record = request.result as IndexedStateRecord | undefined;
      resolve(record?.state ? migrateState(record.state) : null);
    };
    request.onerror = () => resolve(null);
    tx.onerror = () => resolve(null);
  });
}

export async function deleteIndexedState(storageKey: string): Promise<boolean> {
  const db = await openDb();
  if (!db) {
    return false;
  }
  const tx = startTransaction(db, "readwrite");
  if (!tx) {
    return false;
  }

  return new Promise((resolve) => {
    tx.objectStore(STORE_NAME).delete(storageKey);
    tx.oncomplete = () => resolve(true);
    tx.onerror = () => resolve(false);
    tx.onabort = () => resolve(false);
  });
}
